import React, { useState, useMemo } from 'react'
import { ChartTooltip, TooltipData } from './ChartTooltip'

export interface TimeSeriesDataPoint {
  date: string
  [key: string]: number | string
}

interface PerformanceTrendChartProps {
  data: TimeSeriesDataPoint[]
  activeMetrics: {
    key: string
    label: string
    color: string
    useNormalization?: boolean // If false, show raw values instead of normalized 0-100%
  }[]
  height?: number
  comparisonData?: TimeSeriesDataPoint[] | null
}

export function PerformanceTrendChart({
  data,
  activeMetrics,
  height = 400,
  comparisonData = null
}: PerformanceTrendChartProps) {
  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    data: TooltipData
    x: number
    y: number
  } | null>(null)

  // Build per-metric scale map (for independent normalization or absolute values)
  // MUST be before conditional returns to maintain hook order
  const metricScales = useMemo(() => {
    const scales = new Map<string, { min: number; max: number; useNormalization: boolean }>()

    activeMetrics.forEach(metric => {
      // Calculate stats for current data
      const currentValues = data.map(d => Number(d[metric.key]) || 0)
      let min = Math.min(...currentValues)
      let max = Math.max(...currentValues)

      // Also consider comparison data if present
      if (comparisonData) {
        const compValues = comparisonData.map(d => Number(d[metric.key]) || 0)
        min = Math.min(min, Math.min(...compValues))
        max = Math.max(max, Math.max(...compValues))
      }

      // If not using normalization, force min to 0 for better visualization
      const useNormalization = metric.useNormalization !== false // Default to true
      if (!useNormalization) {
        min = 0
      }

      scales.set(metric.key, { min, max, useNormalization })
    })

    return scales
  }, [activeMetrics, data, comparisonData])

  // Early return AFTER hooks
  if (data.length === 0 || activeMetrics.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-brand-dark-light rounded-monumenta border border-brand-gray/10"
        style={{ height: `${height}px` }}
      >
        <p className="text-brand-gray">Selecione uma métrica para visualizar o gráfico</p>
      </div>
    )
  }

  // SVG dimensions - using actual pixel values
  const svgWidth = 1000
  const svgHeight = height
  const padding = { top: 20, right: 40, bottom: 50, left: 60 }
  const chartWidth = svgWidth - padding.left - padding.right
  const chartHeight = svgHeight - padding.top - padding.bottom

  // Normalize value to 0-1 range based on metric's own scale
  const normalizeValue = (value: number, metricKey: string): number => {
    const scale = metricScales.get(metricKey)
    if (!scale) return 0.5

    const range = scale.max - scale.min
    if (range === 0) return 0.5 // Middle if constant

    return (value - scale.min) / range // Returns 0-1
  }

  // Color mapping
  const colorMap: { [key: string]: string } = {
    'brand-primary': '#D2FA52',
    'brand-blue': '#4A9EFF',
    'success': '#10B981',
    'warning': '#F59E0B',
    'error': '#EF4444'
  }

  // Calculate Y position using per-metric normalized scale (0-1)
  const getYPosition = (value: number, metricKey: string) => {
    const normalized = normalizeValue(value, metricKey)
    return padding.top + chartHeight - (normalized * chartHeight)
  }

  const getXPosition = (index: number, dataLength: number) => {
    return padding.left + (index / (dataLength - 1)) * chartWidth
  }

  // Tooltip handlers
  const handlePointHover = (
    point: TimeSeriesDataPoint,
    index: number,
    event: React.MouseEvent<SVGGElement>
  ) => {
    const rect = event.currentTarget.ownerSVGElement?.getBoundingClientRect()
    if (!rect) return

    const xPos = getXPosition(index, data.length)

    // Calculate screen coordinates
    const screenX = rect.left + (xPos / svgWidth) * rect.width
    const screenY = rect.top + 20 // Add some offset from top

    // Build tooltip data with all active metrics
    const tooltipData: TooltipData = {
      date: point.date,
      metrics: activeMetrics.map(metric => {
        const value = Number(point[metric.key]) || 0
        const scale = metricScales.get(metric.key)
        const normalizedPercent = scale ? normalizeValue(value, metric.key) * 100 : undefined

        return {
          label: metric.label,
          value: value,
          color: metric.color,
          isComparison: false,
          normalizedPercent: normalizedPercent,
          scale: scale
        }
      })
    }

    // Add comparison data if available
    if (comparisonData && comparisonData[index]) {
      const compPoint = comparisonData[index]
      activeMetrics.forEach(metric => {
        tooltipData.metrics.push({
          label: metric.label,
          value: Number(compPoint[metric.key]) || 0,
          color: metric.color,
          isComparison: true
        })
      })
    }

    setTooltip({
      data: tooltipData,
      x: screenX,
      y: screenY
    })
  }

  const handlePointLeave = () => {
    setTooltip(null)
  }

  return (
    <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
      <svg
        className="w-full"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ height: `${height}px` }}
      >
        {/* Background */}
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={chartHeight}
          fill="rgba(15, 23, 42, 0.3)"
          rx="4"
        />

        {/* Grid Lines */}
        {Array.from({ length: 5 }, (_, i) => {
          const y = padding.top + (i / 4) * chartHeight
          return (
            <line
              key={i}
              x1={padding.left}
              y1={y}
              x2={padding.left + chartWidth}
              y2={y}
              stroke="rgba(131, 137, 152, 0.1)"
              strokeWidth="1"
            />
          )
        })}

        {/* Vertical grid lines */}
        {data.map((_, index) => {
          const x = getXPosition(index, data.length)
          return (
            <line
              key={index}
              x1={x}
              y1={padding.top}
              x2={x}
              y2={padding.top + chartHeight}
              stroke="rgba(131, 137, 152, 0.05)"
              strokeWidth="1"
            />
          )
        })}

        {/* X-axis labels */}
        {data.map((point, index) => {
          const x = getXPosition(index, data.length)
          return (
            <text
              key={index}
              x={x}
              y={padding.top + chartHeight + 25}
              textAnchor="middle"
              fill="#838998"
              fontSize="12"
              fontFamily="system-ui"
            >
              {point.date}
            </text>
          )
        })}

        {/* Plot comparison lines (dashed, lighter) */}
        {comparisonData && activeMetrics.map((metric) => {
          const strokeColor = colorMap[metric.color] || '#D2FA52'
          const lighterColor = `${strokeColor}60`

          // Build path for comparison data
          const pathCommands = comparisonData.map((point, index) => {
            const x = getXPosition(index, comparisonData.length)
            const value = Number(point[metric.key]) || 0
            const y = getYPosition(value, metric.key)
            return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`
          }).join(' ')

          return (
            <g key={`comparison-${metric.key}`}>
              {/* Dashed line for comparison */}
              <path
                d={pathCommands}
                fill="none"
                stroke={lighterColor}
                strokeWidth="2"
                strokeDasharray="5,5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.6"
              />

              {/* Smaller data points */}
              {comparisonData.map((point, index) => {
                const x = getXPosition(index, comparisonData.length)
                const value = Number(point[metric.key]) || 0
                const y = getYPosition(value, metric.key)

                return (
                  <circle
                    key={index}
                    cx={x}
                    cy={y}
                    r="3"
                    fill={lighterColor}
                    opacity="0.6"
                  >
                    <title>{`${metric.label} (comparação): ${value.toLocaleString()} (${point.date})`}</title>
                  </circle>
                )
              })}
            </g>
          )
        })}

        {/* Plot current period lines (main) */}
        {activeMetrics.map((metric) => {
          const strokeColor = colorMap[metric.color] || '#D2FA52'

          // Build path
          const pathCommands = data.map((point, index) => {
            const x = getXPosition(index, data.length)
            const value = Number(point[metric.key]) || 0
            const y = getYPosition(value, metric.key)
            return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`
          }).join(' ')

          return (
            <g key={metric.key}>
              {/* Line with glow effect */}
              <path
                d={pathCommands}
                fill="none"
                stroke={strokeColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-all duration-300"
                style={{
                  filter: `drop-shadow(0 0 4px ${strokeColor}40)`
                }}
              />

              {/* Data points with interactive hover areas */}
              {data.map((point, index) => {
                const x = getXPosition(index, data.length)
                const value = Number(point[metric.key]) || 0
                const y = getYPosition(value, metric.key)

                return (
                  <g
                    key={index}
                    onMouseEnter={(e) => handlePointHover(point, index, e)}
                    onMouseLeave={handlePointLeave}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Invisible hover area (larger hit target) */}
                    <circle
                      cx={x}
                      cy={y}
                      r="16"
                      fill="transparent"
                      className="transition-all duration-200"
                    />
                    {/* Outer glow circle */}
                    <circle
                      cx={x}
                      cy={y}
                      r="8"
                      fill={strokeColor}
                      opacity="0.2"
                      className="transition-all duration-200 group-hover:opacity-40"
                      pointerEvents="none"
                    />
                    {/* Main point */}
                    <circle
                      cx={x}
                      cy={y}
                      r="4"
                      fill={strokeColor}
                      stroke="#0f172a"
                      strokeWidth="2"
                      className="transition-all duration-200"
                      pointerEvents="none"
                    />
                  </g>
                )
              })}
            </g>
          )
        })}

        {/* Y-axis labels - normalized % or absolute values based on first metric */}
        {activeMetrics.length > 0 && Array.from({ length: 5 }, (_, i) => {
          const y = padding.top + (i / 4) * chartHeight
          const firstMetric = activeMetrics[0]
          const scale = metricScales.get(firstMetric.key)

          let label = ''
          if (scale?.useNormalization) {
            // Show normalized percentage
            const percentage = 100 * (1 - i / 4)
            label = `${percentage.toFixed(0)}%`
          } else if (scale) {
            // Show absolute value
            const value = scale.min + (scale.max - scale.min) * (1 - i / 4)
            if (value >= 1000000) {
              label = `${(value / 1000000).toFixed(1)}M`
            } else if (value >= 1000) {
              label = `${(value / 1000).toFixed(0)}K`
            } else {
              label = value.toFixed(0)
            }
          }

          return (
            <text
              key={i}
              x={padding.left - 10}
              y={y + 4}
              textAnchor="end"
              fill="#838998"
              fontSize="11"
              fontFamily="system-ui"
            >
              {label}
            </text>
          )
        })}
      </svg>

      {/* Legend with Scale Indicators */}
      <div className="mt-6 pt-4 border-t border-brand-gray/20">
        {/* Title */}
        {activeMetrics.length > 0 && (() => {
          const firstMetric = activeMetrics[0]
          const scale = metricScales.get(firstMetric.key)
          const isNormalized = scale?.useNormalization !== false

          return (
            <div className="text-center text-xs text-brand-gray/60 mb-3 font-medium">
              {isNormalized
                ? 'Escala Normalizada (0-100% do alcance de cada métrica)'
                : 'Valores Absolutos'}
            </div>
          )
        })()}

        {/* Metrics with scale ranges */}
        <div className="flex items-start justify-center flex-wrap gap-6">
          {activeMetrics.map((metric) => {
            const bgColorMap: { [key: string]: string } = {
              'brand-primary': 'bg-brand-primary',
              'brand-blue': 'bg-brand-blue',
              'success': 'bg-success',
              'warning': 'bg-warning',
              'error': 'bg-error'
            }

            const scale = metricScales.get(metric.key)

            // Format scale values
            const formatScaleValue = (value: number): string => {
              if (value > 1000) {
                return new Intl.NumberFormat('pt-BR', {
                  notation: 'compact',
                  maximumFractionDigits: 1
                }).format(value)
              }
              return value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
            }

            return (
              <div key={metric.key} className="flex flex-col items-center space-y-1">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${bgColorMap[metric.color]}`}></div>
                  <span className="text-sm text-brand-gray font-medium">{metric.label}</span>
                </div>
                {scale && (
                  <div className="text-[10px] text-brand-gray/60 font-mono">
                    {formatScaleValue(scale.min)} - {formatScaleValue(scale.max)}
                  </div>
                )}
              </div>
            )
          })}
          {comparisonData && (
            <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-brand-gray/20">
              <div className="w-6 h-0.5 bg-brand-gray opacity-60" style={{ backgroundImage: 'repeating-linear-gradient(to right, currentColor 0, currentColor 5px, transparent 5px, transparent 10px)' }}></div>
              <span className="text-sm text-brand-gray font-medium italic">Período de comparação</span>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip */}
      <ChartTooltip
        data={tooltip?.data || null}
        x={tooltip?.x || 0}
        y={tooltip?.y || 0}
        visible={!!tooltip}
      />
    </div>
  )
}
